import React, { useEffect, useRef, useState } from "react";

// Chart primitives for the Overview. Plain SVG/HTML, no chart library.
// Specs follow the dataviz method: one validated hue (#2a78d6 on white),
// hairline solid grid, 2px line + 10% area wash, bars <= 24px thick with a
// 4px rounded data-end and a square baseline, values at the bar tip, a hover
// AND keyboard tooltip on every chart, and a table-view twin for each.

export const formatNumber = (n) => new Intl.NumberFormat("en-IN").format(n);
export const formatPercent = (fraction) => `${Math.round(fraction * 100)}%`;

// ----- Project colours (categorical: identity) ---------------------------------
// The reference categorical order, validated for adjacent pairs on the white
// card (worst CVD dE 9.1, normal-vision dE 19.6). Colour follows the project,
// never its rank: the four original projects hold fixed slots, admin-added
// ones take the next slots in creation order, so filtering never repaints a
// project. Past eight projects, the rest share the de-emphasis gray.
const PROJECT_SLOTS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
const FIXED_PROJECTS = ["Vatika Seven Elements", "Sovereign Park", "Saan Verdante", "Inframantra HO"];
export const NOT_SET = "Not set";
const MUTED_MARK = "#8a8880"; // 3.55:1 on white

export const makeProjectColors = (projectNames = []) => {
  const order = [...FIXED_PROJECTS, ...projectNames.filter((n) => !FIXED_PROJECTS.includes(n))];
  const map = {};
  order.forEach((name, i) => {
    map[name] = PROJECT_SLOTS[i] || MUTED_MARK;
  });
  return {
    order: [...order, NOT_SET],
    colorOf: (name) => map[name] || MUTED_MARK,
  };
};

// Legend: rect swatches (they key bars), names in text ink
export const Legend = ({ items, colorOf }) => (
  <ul className="viz-legend" aria-label="Legend">
    {items.map((name) => (
      <li key={name}>
        <span className="viz-swatch" style={{ background: colorOf(name) }} aria-hidden="true" />
        {name}
      </li>
    ))}
  </ul>
);

// Round y-axis ticks to clean 1/2/5 steps
const niceTicks = (max, count = 4) => {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / magnitude;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * magnitude;
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(Math.round(v));
  return ticks;
};

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

// ----- Card with title + table-view toggle -----------------------------------
export const ChartCard = ({ title, subtitle, table, children, className = "" }) => {
  const [showTable, setShowTable] = useState(false);
  return (
    <section className={`viz-card ${className}`.trim()}>
      <header className="viz-card-head">
        <div>
          <h3 className="viz-card-title">{title}</h3>
          {subtitle && <p className="viz-card-sub">{subtitle}</p>}
        </div>
        {table && (
          <button
            type="button"
            className="viz-toggle"
            aria-pressed={showTable}
            onClick={() => setShowTable((v) => !v)}
          >
            {showTable ? "Chart" : "Table"}
          </button>
        )}
      </header>
      {showTable && table ? <DataTable {...table} /> : children}
    </section>
  );
};

export const DataTable = ({ columns, rows }) => (
  <div className="viz-table-wrap">
    <table className="viz-table">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ----- Stat tile ----------------------------------------------------------------
// delta: { value, previous, periodLabel } — up is good for every KPI here.
export const StatTile = ({ label, value, sub, delta }) => {
  let deltaNode = null;
  if (delta && delta.previous != null) {
    if (delta.previous === 0) {
      deltaNode = delta.value > 0 ? <span className="viz-delta viz-delta--up">▲ New {delta.periodLabel}</span> : null;
    } else {
      const change = (delta.value - delta.previous) / delta.previous;
      const up = change >= 0;
      deltaNode = (
        <span className={`viz-delta ${up ? "viz-delta--up" : "viz-delta--down"}`}>
          {up ? "▲" : "▼"} {Math.abs(Math.round(change * 100))}% {delta.periodLabel}
        </span>
      );
    }
  }
  return (
    <div className="viz-stat">
      <span className="viz-stat-label">{label}</span>
      <span className="viz-stat-value">{value}</span>
      {deltaNode}
      {sub && <span className="viz-stat-sub">{sub}</span>}
    </div>
  );
};

// ----- Area chart (single series over time) -------------------------------------
// points: [{ label, fullLabel, value, breakdown: [{ label, value }] }]
export const AreaChart = ({ points, height = 250, valueLabel = "visits", colorOf }) => {
  const [ref, width] = useWidth();
  const [active, setActive] = useState(null);

  const pad = { top: 18, right: 18, bottom: 30, left: 44 };
  const w = Math.max(width, 280);
  const innerW = w - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const ticks = niceTicks(Math.max(...points.map((p) => p.value), 0));
  const top = ticks[ticks.length - 1] || 1;
  const x = (i) => pad.left + (points.length <= 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v) => pad.top + innerH - (v / top) * innerH;

  const line = points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const area = points.length
    ? `${line} L${x(points.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`
    : "";

  // ~6 x-axis labels, evenly spaced, always including the last
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(innerW / 90))));
  const showLabel = (i) => i % labelEvery === 0 || i === points.length - 1;

  const nearest = (clientX) => {
    const box = ref.current.getBoundingClientRect();
    const px = clientX - box.left - pad.left;
    const i = points.length <= 1 ? 0 : Math.round((px / innerW) * (points.length - 1));
    return Math.min(points.length - 1, Math.max(0, i));
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      setActive((current) => {
        const from = current == null ? points.length - 1 : current;
        const next = from + (event.key === "ArrowRight" ? 1 : -1);
        return Math.min(points.length - 1, Math.max(0, next));
      });
    }
  };

  const point = active != null ? points[active] : null;
  const tipLeft = point ? Math.min(Math.max(x(active), 90), w - 90) : 0;

  return (
    <div className="viz-area" ref={ref}>
      <svg
        width={w}
        height={height}
        role="img"
        aria-label={`${valueLabel} over time; use left and right arrow keys to read values`}
        tabIndex={0}
        onPointerMove={(e) => setActive(nearest(e.clientX))}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive(points.length - 1)}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className="viz-grid" x1={pad.left} x2={w - pad.right} y1={y(t)} y2={y(t)} />
            <text className="viz-tick" x={pad.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
              {formatNumber(t)}
            </text>
          </g>
        ))}
        {points.map((p, i) =>
          showLabel(i) ? (
            <text
              key={p.label + i}
              className="viz-tick"
              x={x(i)}
              y={height - 8}
              textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
            >
              {p.label}
            </text>
          ) : null
        )}
        <path className="viz-area-fill" d={area} />
        <path className="viz-area-line" d={line} />
        {point && (
          <>
            <line className="viz-crosshair" x1={x(active)} x2={x(active)} y1={pad.top} y2={pad.top + innerH} />
            <circle className="viz-marker" cx={x(active)} cy={y(point.value)} r={5} />
          </>
        )}
      </svg>
      {point && (
        <div className="viz-tooltip" style={{ left: tipLeft, top: 6 }} role="status">
          <strong className="viz-tooltip-value">
            {formatNumber(point.value)} {valueLabel}
          </strong>
          <span className="viz-tooltip-label">{point.fullLabel}</span>
          {point.breakdown?.length > 0 && (
            <ul className="viz-tooltip-rows">
              {point.breakdown.map((row) => (
                <li key={row.label}>
                  <span>
                    {colorOf && (
                      <i className="viz-key" style={{ background: colorOf(row.label) }} aria-hidden="true" />
                    )}
                    {row.label}
                  </span>
                  <b>{formatNumber(row.value)}</b>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ----- Horizontal bar list (nominal categories) ---------------------------------
// colorOf: colour each bar by its entity (used for projects, so the bars match
// the project legend elsewhere). showShare: print "729 · 87%" at the tip.
export const BarList = ({ rows, total, unit = "", colorOf, showShare = false }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="viz-barlist" style={{ "--value-w": showShare ? "86px" : "52px" }}>
      {rows.map((row) => {
        const share = total ? ` · ${formatPercent(row.value / total)} of ${unit || "total"}` : "";
        return (
          <li
            key={row.label}
            className="viz-barlist-row"
            tabIndex={0}
            data-tip={`${row.label}: ${formatNumber(row.value)}${share}`}
            aria-label={`${row.label}: ${formatNumber(row.value)}${share}`}
          >
            <span className="viz-barlist-label">{row.label}</span>
            <span className="viz-barlist-track">
              <span
                className="viz-bar viz-bar--h"
                style={{ "--pct": row.value / max, ...(colorOf && { background: colorOf(row.label) }) }}
              />
              <span className="viz-barlist-value">
                {formatNumber(row.value)}
                {showShare && total ? (
                  <span className="viz-barlist-share"> · {formatPercent(row.value / total)}</span>
                ) : null}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// Part-to-whole per row: each bar splits into project segments (2px surface
// gap between them, 4px rounded data-end), total at the tip.
export const StackedBarList = ({ rows, order, colorOf }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="viz-barlist" style={{ "--value-w": "52px" }}>
      {rows.map((row) => {
        const parts = order
          .filter((project) => row.byProject?.[project])
          .map((project) => ({ project, value: row.byProject[project] }));
        const tip = `${row.label}: ${formatNumber(row.value)} — ${parts
          .map((p) => `${p.project} ${formatNumber(p.value)}`)
          .join(" · ")}`;
        return (
          <li key={row.label} className="viz-barlist-row" tabIndex={0} data-tip={tip} aria-label={tip}>
            <span className="viz-barlist-label">{row.label}</span>
            <span className="viz-barlist-track">
              <span className="viz-stack" style={{ "--pct": row.value / max }}>
                {parts.map((p) => (
                  <span
                    key={p.project}
                    className="viz-seg"
                    style={{ flexGrow: p.value, background: colorOf(p.project) }}
                  />
                ))}
              </span>
              <span className="viz-barlist-value">{formatNumber(row.value)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// ----- Column chart (ordered categories) -----------------------------------------
export const ColumnChart = ({ rows, total, unit = "", height = 190 }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="viz-columns" style={{ "--plot-h": `${height}px` }}>
      {rows.map((row) => {
        const share = total ? ` · ${formatPercent(row.value / total)} of ${unit || "total"}` : "";
        return (
          <div
            key={row.label}
            className="viz-column"
            tabIndex={0}
            data-tip={`${row.label}: ${formatNumber(row.value)}${share}`}
            aria-label={`${row.label}: ${formatNumber(row.value)}${share}`}
          >
            <div className="viz-column-plot">
              <span className="viz-column-value">{formatNumber(row.value)}</span>
              <span className="viz-bar viz-bar--v" style={{ "--pct": row.value / max }} />
            </div>
            <span className="viz-column-label">{row.label}</span>
          </div>
        );
      })}
    </div>
  );
};
